'use client'

import * as React from 'react'

import { Button } from './Button'
import { cn } from './cn'

/** Why a file was left out: wrong type, too large, or one more than allowed. */
export type FileRejection = { file: File; reason: 'type' | 'size' | 'count' }

type Limits = { accept?: string; maxSize?: number; multiple: boolean; maxFiles?: number }

/**
 * Whether a file matches an `accept` list as the browser's file dialog reads
 * it: `.pdf` by name, `image/*` by type family, `application/pdf` by exact
 * type. A drop bypasses the dialog's filter, so the same test runs here.
 */
export function acceptsFile(file: Pick<File, 'name' | 'type'>, accept: string | undefined): boolean {
  const tokens = (accept ?? '')
    .split(',')
    .map(token => token.trim().toLowerCase())
    .filter(token => token !== '')
  if (tokens.length === 0) return true
  const name = file.name.toLowerCase()
  const type = file.type.toLowerCase()
  return tokens.some(token => {
    if (token.startsWith('.')) return name.endsWith(token)
    if (token.endsWith('/*')) return type.startsWith(token.slice(0, -1))
    return type === token
  })
}

const sameFile = (a: File, b: File): boolean =>
  a.name === b.name && a.size === b.size && a.lastModified === b.lastModified

/**
 * The list after `incoming` is added to `current`. A single-file drop replaces
 * what was there; a multi-file drop appends, skips a file already in the list
 * and stops at `maxFiles`. Everything left out comes back with its reason.
 */
export function addFiles(
  current: readonly File[],
  incoming: readonly File[],
  limits: Limits
): { files: File[]; rejected: FileRejection[] } {
  const rejected: FileRejection[] = []
  const valid: File[] = []
  for (const file of incoming) {
    if (!acceptsFile(file, limits.accept)) rejected.push({ file, reason: 'type' })
    else if (limits.maxSize !== undefined && file.size > limits.maxSize) rejected.push({ file, reason: 'size' })
    else valid.push(file)
  }

  if (!limits.multiple) {
    const [first, ...rest] = valid
    for (const file of rest) rejected.push({ file, reason: 'count' })
    return { files: first === undefined ? [...current] : [first], rejected }
  }

  const files = [...current]
  for (const file of valid) {
    if (files.some(existing => sameFile(existing, file))) continue
    if (limits.maxFiles !== undefined && files.length >= limits.maxFiles) rejected.push({ file, reason: 'count' })
    else files.push(file)
  }
  return { files, rejected }
}

/** A file size the way people read it: "820 B", "12 kB", "3.4 MB", in the given locale. */
export function formatFileSize(bytes: number, locale?: string): string {
  const [value, unit] =
    bytes < 1000 ? [bytes, 'byte'] : bytes < 1_000_000 ? [bytes / 1000, 'kilobyte'] : [bytes / 1_000_000, 'megabyte']
  return new Intl.NumberFormat(locale, { style: 'unit', unit, maximumFractionDigits: 1 }).format(value)
}

function UploadIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden className={className}>
      <path d="M12 15V4" />
      <path d="m7 9 5-5 5 5" />
      <path d="M5 15v3a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3" />
    </svg>
  )
}

function FileIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden className={className}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
    </svg>
  )
}

function RemoveIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  )
}

export type FileDropProps = {
  /** The chosen files. The component holds none itself; send them with the form yourself. */
  files: readonly File[]
  onFilesChange: (files: File[]) => void
  /** Files left out on a drop or a pick. Say why next to the area; the component writes no text. */
  onReject?: (rejections: FileRejection[]) => void
  /** The same list as on `<input type="file">`, e.g. ".pdf,image/*". Also checked on a drop. */
  accept?: string
  multiple?: boolean
  /** Largest size per file, in bytes. */
  maxSize?: number
  /** Most files in the list, with `multiple`. */
  maxFiles?: number
  disabled?: boolean
  /** Marks the area as wrong, e.g. a required file is missing. */
  invalid?: boolean
  id?: string
  /** The prompt in the area, e.g. "Drop a PDF here or choose one". */
  children: React.ReactNode
  /** A second, quieter line, e.g. the allowed types and size. */
  hint?: React.ReactNode
  /** The accessible name of a file's remove button, e.g. `file => \`Remove ${file.name}\``. */
  removeLabel: (file: File) => string
  /** Locale for the file sizes; the page's language, not the browser's. */
  locale?: string
  className?: string
}

/**
 * An area to drop files on or click to choose them, with the chosen files
 * listed below it. The file input stays inside, visually hidden, so the
 * keyboard reaches it with Tab and opens the dialog with Space or Enter.
 */
export function FileDrop({
  files,
  onFilesChange,
  onReject,
  accept,
  multiple = false,
  maxSize,
  maxFiles,
  disabled = false,
  invalid = false,
  id,
  children,
  hint,
  removeLabel,
  locale,
  className
}: FileDropProps) {
  const generatedId = React.useId()
  const inputId = id ?? generatedId
  const hintId = `${inputId}-hint`
  const [dragging, setDragging] = React.useState(false)
  // dragenter and dragleave fire for every child the pointer crosses; counting
  // them is what keeps the highlight from flickering over the icon and text.
  const depth = React.useRef(0)

  const take = (incoming: File[]) => {
    if (incoming.length === 0) return
    const result = addFiles(files, incoming, { accept, maxSize, multiple, maxFiles })
    onFilesChange(result.files)
    if (result.rejected.length > 0) onReject?.(result.rejected)
  }

  const carriesFiles = (event: React.DragEvent) => Array.from(event.dataTransfer.types).includes('Files')

  const onDragEnter = (event: React.DragEvent<HTMLLabelElement>) => {
    if (disabled || !carriesFiles(event)) return
    event.preventDefault()
    depth.current += 1
    setDragging(true)
  }
  const onDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
    if (disabled || !carriesFiles(event)) return
    event.preventDefault()
    event.dataTransfer.dropEffect = 'copy'
  }
  const onDragLeave = () => {
    if (disabled) return
    depth.current = Math.max(0, depth.current - 1)
    if (depth.current === 0) setDragging(false)
  }
  const onDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    if (disabled) return
    event.preventDefault()
    depth.current = 0
    setDragging(false)
    take(Array.from(event.dataTransfer.files))
  }

  return (
    <div data-slot="file-drop" className={cn('flex flex-col gap-2', className)}>
      <label
        htmlFor={inputId}
        data-dragging={dragging || undefined}
        data-disabled={disabled || undefined}
        data-invalid={invalid || undefined}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={cn(
          'group flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 px-6 py-8 text-center text-sm text-gray-500 transition-colors',
          'hover:border-gray-300 hover:bg-gray-100',
          'has-[:focus-visible]:border-ring has-[:focus-visible]:ring-[3px] has-[:focus-visible]:ring-ring/50',
          'data-[dragging]:border-blue-500 data-[dragging]:bg-blue-50 data-[dragging]:text-blue-700',
          'data-[invalid]:border-destructive',
          'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[disabled]:hover:border-gray-200 data-[disabled]:hover:bg-gray-50',
          'avex:rounded-avex-lg avex:border-[1.5px] avex:border-line-control avex:bg-surface-sunken avex:text-ink-secondary',
          'avex:hover:border-line-strong avex:hover:bg-surface-muted',
          'avex:has-[:focus-visible]:border-ink avex:has-[:focus-visible]:ring-surface-sunken',
          'avex:data-[dragging]:border-brand avex:data-[dragging]:bg-brand-soft avex:data-[dragging]:text-brand-soft-ink',
          'avex:data-[invalid]:border-danger',
          'avex:data-[disabled]:hover:border-line-control avex:data-[disabled]:hover:bg-surface-sunken'
        )}
      >
        <UploadIcon className="mb-1 size-6 text-gray-400 group-data-[dragging]:text-current avex:text-ink-muted avex:group-data-[dragging]:text-current" />
        <span className="font-medium text-gray-900 avex:text-ink">{children}</span>
        {hint !== undefined && (
          <span id={hintId} className="text-xs text-gray-500 avex:text-ink-muted">
            {hint}
          </span>
        )}
        <input
          id={inputId}
          type="file"
          className="sr-only"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          aria-describedby={hint !== undefined ? hintId : undefined}
          onChange={event => {
            take(Array.from(event.target.files ?? []))
            // Cleared, so choosing the same file again after removing it fires again.
            event.target.value = ''
          }}
        />
      </label>

      {files.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {files.map(file => (
            <li
              key={`${file.name}-${file.size}-${file.lastModified}`}
              className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-3 py-2 text-sm avex:rounded-avex-sm avex:border-line avex:bg-surface"
            >
              <FileIcon className="size-4 shrink-0 text-gray-400 avex:text-ink-muted" />
              <span className="min-w-0 flex-1 truncate text-gray-900 avex:text-ink" title={file.name}>
                {file.name}
              </span>
              <span className="shrink-0 text-xs tabular-nums text-gray-500 avex:text-ink-muted">
                {formatFileSize(file.size, locale)}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 shrink-0"
                disabled={disabled}
                aria-label={removeLabel(file)}
                onClick={() => onFilesChange(files.filter(other => other !== file))}
              >
                <RemoveIcon />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
